import { ReactNode } from "react";
import { Button } from "primereact/button";

/**
 * SYSTEM_DESIGN.md §6.1 / INC-01 — Encabezado de página.
 *
 * Unifica las 6 variantes que convivían. Título centrado con botón `+` circular
 * inmerso en el bloque, sin texto adyacente.
 *
 * No hay prop para ponerle label al botón de crear: el §6.1 dice "siempre un
 * ícono circular + (sin texto adyacente)". Si hiciera falta un label, sería una
 * excepción al spec y debe discutirse, no resolverse con una prop.
 */
export interface PageHeaderAction {
  icon: string;
  onClick: () => void;
  /** Va al aria-label y al tooltip: el botón no muestra texto. */
  label: string;
  disabled?: boolean;
  /**
   * Relleno de marca en vez de outline. Para la acción principal de una
   * pantalla sin `+` (ej. "Editar" en un detalle): un único ícono outline
   * suelto junto al título se pierde contra el fondo.
   */
  filled?: boolean;
}

interface PageHeaderProps {
  title: string;
  /** Acción de crear. Se renderiza como `+` circular contiguo al título. */
  onCreate?: () => void;
  /** Texto accesible del botón crear (no visible). Ej: "Crear beneficio". */
  createLabel?: string;
  /** Acción de crear bloqueada (ej: header en "Todos" sin tenant). */
  createDisabled?: boolean;
  /** Motivo del bloqueo: reemplaza al tooltip del botón. */
  createDisabledReason?: string;
  /** §6.1 — acciones secundarias: íconos circulares contiguos al `+`. */
  secondaryActions?: PageHeaderAction[];
  /** Badge de estado junto al título (pantallas de detalle). */
  badge?: ReactNode;
}

export const PageHeader = ({
  title,
  onCreate,
  createLabel = "Crear",
  createDisabled = false,
  createDisabledReason,
  secondaryActions = [],
  badge,
}: PageHeaderProps) => (
  <div className="flex flex-wrap w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
    <h1 className="text-center m-0">{title}</h1>
    {badge}
    {onCreate && (
      <Button
        icon="pi pi-plus"
        rounded
        size="small"
        onClick={onCreate}
        disabled={createDisabled}
        aria-label={createLabel}
        tooltip={createDisabled ? createDisabledReason ?? createLabel : createLabel}
        tooltipOptions={{ position: "bottom", showOnDisabled: Boolean(createDisabledReason) }}
      />
    )}
    {secondaryActions.map((a) => (
      <Button
        key={a.label}
        icon={a.icon}
        rounded
        outlined={!a.filled}
        size="small"
        onClick={a.onClick}
        disabled={a.disabled}
        aria-label={a.label}
        tooltip={a.label}
        tooltipOptions={{ position: "bottom" }}
      />
    ))}
  </div>
);
